import { ArrowLeft, FileText, Layers, Loader2, Search, Settings, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnalysisTab } from "@/components/sowift/AnalysisTab";
import { ContextTab } from "@/components/sowift/ContextTab";
import type { TabId } from "@/components/sowift/constants";
import { STATUS_COLORS, STATUS_LABELS } from "@/components/sowift/constants";
import { DocumentsTab } from "@/components/sowift/DocumentsTab";
import { OverviewTab } from "@/components/sowift/OverviewTab";
import { sowiftService } from "@/lib/services";
import type { SowiftContextItemCreate, SowiftProjectResponse } from "@/types";

export function SowiftProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<SowiftProjectResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showAddContext, setShowAddContext] = useState(false);
  const [addingContext, setAddingContext] = useState(false);
  const [deletingItem, setDeletingItem] = useState<string | null>(null);
  const [showDeleteProject, setShowDeleteProject] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);

  const [contextForm, setContextForm] = useState<SowiftContextItemCreate>({
    contextType: "meeting_notes",
    title: "",
    content: "",
  });

  useEffect(() => {
    if (projectId) loadProject();
  }, [projectId]);

  // Poll for pending summaries to complete
  useEffect(() => {
    if (!project) return;
    const hasPending = project.contextItems.some((item) => item.summaryStatus === "pending" && !item.aiSummary);
    if (!hasPending) return;

    const interval = setInterval(async () => {
      if (!projectId) return;
      try {
        const response = await sowiftService.getProject(projectId);
        setProject(response.data);
      } catch {
        // Silently ignore polling errors
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [project, projectId]);

  const loadProject = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const response = await sowiftService.getProject(projectId);
      setProject(response.data);
    } catch (error) {
      console.error("Failed to load project:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContext = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !contextForm.title.trim() || !contextForm.content.trim()) return;

    try {
      setAddingContext(true);
      await sowiftService.createContextItem(projectId, contextForm);
      setShowAddContext(false);
      setContextForm({ contextType: "meeting_notes", title: "", content: "" });
      await loadProject();
    } catch (error) {
      console.error("Failed to add context:", error);
    } finally {
      setAddingContext(false);
    }
  };

  const handleDeleteContext = async (itemUuid: string) => {
    if (!projectId) return;
    try {
      setDeletingItem(itemUuid);
      await sowiftService.deleteContextItem(projectId, itemUuid);
      await loadProject();
    } catch (error) {
      console.error("Failed to delete context item:", error);
    } finally {
      setDeletingItem(null);
    }
  };

  const handleRetrySummary = async (itemUuid: string) => {
    if (!projectId) return;
    try {
      await sowiftService.retrySummary(projectId, itemUuid);
      await loadProject();
    } catch (error) {
      console.error("Failed to retry summary:", error);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectId) return;
    try {
      setDeletingProject(true);
      await sowiftService.deleteProject(projectId);
      navigate("/apps/sowift");
    } catch (error) {
      console.error("Failed to delete project:", error);
    } finally {
      setDeletingProject(false);
    }
  };

  const formatCurrency = (value: number | null | undefined, currency: string) => {
    if (value == null) return "---";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <h3 className="text-lg font-medium text-content-primary">Project not found</h3>
        <button onClick={() => navigate("/apps/sowift")} className="mt-4 text-sm text-brand-primary hover:underline">
          Back to projects
        </button>
      </div>
    );
  }

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: "overview", label: "Overview", icon: Settings },
    { id: "context", label: "Context", icon: Layers },
    { id: "analysis", label: "Analysis", icon: Search },
    { id: "documents", label: "Documents", icon: FileText },
  ];

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/apps/sowift")}
              className="p-1 text-content-tertiary hover:text-content-primary hover:bg-interactive-active rounded-sm transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-content-primary">{project.name}</h1>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status]}`}>
                  {STATUS_LABELS[project.status]}
                </span>
              </div>
              <p className="text-content-tertiary mt-0.5">{project.clientName}</p>
            </div>
          </div>
          <button
            onClick={() => setShowDeleteProject(true)}
            className="p-2 text-content-quaternary hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
            title="Delete project"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "bg-surface-secondary text-content-primary"
                    : "text-content-tertiary hover:text-content-primary hover:bg-interactive-hover"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.id === "context" && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-surface-tertiary text-xs">
                    {project.contextItems.length}
                  </span>
                )}
                {tab.id === "documents" && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-surface-tertiary text-xs">
                    {project.documents.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === "overview" && (
          <OverviewTab project={project} formatCurrency={formatCurrency} onTabChange={setActiveTab} />
        )}
        {activeTab === "context" && (
          <ContextTab
            project={project}
            showAddContext={showAddContext}
            setShowAddContext={setShowAddContext}
            contextForm={contextForm}
            setContextForm={setContextForm}
            addingContext={addingContext}
            handleAddContext={handleAddContext}
            handleDeleteContext={handleDeleteContext}
            handleRetrySummary={handleRetrySummary}
            deletingItem={deletingItem}
          />
        )}
        {activeTab === "analysis" && (
          <AnalysisTab projectId={projectId!} project={project} onProjectUpdate={loadProject} />
        )}
        {activeTab === "documents" && (
          <DocumentsTab projectId={projectId!} project={project} onProjectUpdate={loadProject} />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteProject && (
        <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50">
          <div className="bg-surface-primary border border-border-secondary rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-medium text-content-primary">Delete Project</h3>
            <p className="text-content-tertiary mt-2 text-sm">
              Are you sure you want to delete &ldquo;{project.name}&rdquo;? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowDeleteProject(false)}
                className="px-4 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm font-medium text-content-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProject}
                disabled={deletingProject}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
              >
                {deletingProject && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
